import type { IAppConfigResponse } from '@modules/config/types';
import type { IContextMutationResult } from '@modules/core/services/query/types';
import { CONFIG_QUERY_KEY } from '@modules/config/constants';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { MERCHANT_SETTINGS_LIST_QUERY_KEY, MERCHANT_SETTINGS_PERMISSIONS } from '@modules/merchant-settings/constants';
import { useToastStore } from '@modules/toast/store';

import type { IMerchantSettingsEditPayload, IMerchantSettingsResponse } from '../types';
import {
  EDIT_MERCHANT_SETTINGS_MUTATION,
  GET_MERCHANT_SETTINGS_QUERY_KEY,
  MERCHANT_SETTINGS_API_URL,
} from '../constants';

export default function useEditMerchantSettingsMutation(): IContextMutationResult<IMerchantSettingsResponse, Error, IMerchantSettingsEditPayload> {
  const { fetchPut } = useRestClient();
  const { displayPresetToast } = useToastStore();

  return useContextMutation<IMerchantSettingsResponse, Error, IMerchantSettingsEditPayload>({
    mutationKey: [EDIT_MERCHANT_SETTINGS_MUTATION],
    mutationFn: ({ id, merchant }) => fetchPut<IMerchantSettingsResponse>(
      MERCHANT_SETTINGS_API_URL,
      merchant,
      {
        permission: MERCHANT_SETTINGS_PERMISSIONS.EDIT,
        urlParams: {
          id,
        },
      },
    ),
    async onSuccess({ queryClient, data, variables }) {
      await queryClient.invalidateQueries({ queryKey: [MERCHANT_SETTINGS_LIST_QUERY_KEY] });

      displayPresetToast('edited');

      if (variables.id) {
        queryClient.setQueryData<IMerchantSettingsResponse>(
          [GET_MERCHANT_SETTINGS_QUERY_KEY, variables.id],
          () => ({
            merchant: { ...data.merchant },
          }),
        );
      }

      queryClient.setQueryData<IAppConfigResponse>([CONFIG_QUERY_KEY], (queryData) => {
        if (!queryData) {
          return queryData;
        }

        let merchantsList = [...queryData.merchants_list];

        if (variables.id) {
          merchantsList = queryData.merchants_list.map((item) => {
            if (item.id === variables.id) {
              return { ...item, ...data.merchant };
            }
            return item;
          });
        }

        return {
          ...queryData,
          merchants_list: merchantsList,
        };
      });
    },
  });
}
